import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router';
import { ChevronDown, ChevronRight, ExternalLink, Loader2, Search, X } from 'lucide-react';
import {
  useAuditTrail as useDirectoryAuditTrail,
  type AuditTrailFilters,
} from '@my/directory-api/hooks';
import { useAuditTrail as useTmsAuditTrail } from '@my/tms-api/hooks';
import type { AuditActionValue, AuditLogEntry } from '@my/tms-api';
import {
  cn,
  ErrorBanner,
  Loading,
  formatAuditActor,
  formatAuditValue,
  parseAuditChanges,
  PageHeader,
  SelectInput,
  TextInput,
} from '@my/ui';
import { getCurrentOrganisationId } from '@/auth/currentOrg';

interface Filters {
  from: string;
  to: string;
  actor: string;
  action: AuditActionValue | '';
  search: string;
}

/** Kept out of the component so the empty state is one stable object. */
const NO_FILTERS: Filters = { from: '', to: '', actor: '', action: '', search: '' };

const ACTION_OPTIONS = [
  { value: '', label: 'Any action' },
  { value: 'Created', label: 'Created' },
  { value: 'Updated', label: 'Updated' },
  { value: 'Deleted', label: 'Deleted' },
  { value: 'Restored', label: 'Restored' },
  { value: 'Overridden', label: 'Overridden' },
];

const ACTION_TONE: Record<AuditLogEntry['action'], string> = {
  Created: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  Updated: 'bg-sky-50 text-sky-700 ring-sky-200',
  Deleted: 'bg-red-50 text-red-700 ring-red-200',
  Restored: 'bg-amber-50 text-amber-700 ring-amber-200',
  Overridden: 'bg-violet-50 text-violet-700 ring-violet-200',
};

/**
 * Record types read as table names here, since the page shows every kind at
 * once. Only the ones whose name does not already read as English are mapped.
 */
const ENTITY_LABELS: Record<string, string> = {
  OrderTask: 'Task',
  OrderTaskCargoItem: 'Cargo',
  OrderTaskResource: 'Assignment',
  OrderBooking: 'Booking',
  OrderChargeCost: 'Charge / cost',
  OrderDocument: 'Document',
  OrderServiceTag: 'Service tag',
  InvoiceLine: 'Invoice line',
  Party: 'Customer / supplier',
  PartyContact: 'Contact',
  PartyDocument: 'Party document',
  CargoDefinition: 'Cargo type',
  CargoDefinitionVariant: 'Cargo variant',
  ChargeCostDefinition: 'Charge type',
  ServiceTag: 'Service tag',
  OrganisationEquipmentType: 'Equipment type',
  OrganisationCarrier: 'Carrier',
  OrganisationRoute: 'Route',
  OrganisationDocumentTemplate: 'Document layout',
  OrganisationBankAccount: 'Bank account',
  OrganisationTaxRate: 'Tax rate',
  OrganisationNominal: 'Nominal code',
  OrganisationSequence: 'Numbering',
  AccountUser: 'Account membership',
  UserAppRole: 'Role',
  ResourceCertification: 'Certification',
  DriverRosterBlock: 'Roster block',
  DriverRosterConfig: 'Roster settings',
  WorkflowPolicy: 'Workflow',
  SubcontractorTrailer: 'Subcontractor trailer',
};

const AGGREGATE_LABELS: Record<string, string> = {
  Order: 'Order',
  Party: 'Customer',
  Organisation: 'Organisation',
  User: 'User',
  Resource: 'Resource',
  Invoice: 'Invoice',
  Load: 'Load',
  Site: 'Site',
  Address: 'Location',
  CargoDefinition: 'Cargo type',
  ServiceTag: 'Service tag',
  WorkflowPolicy: 'Workflow',
};

/** Where an entry's record can be opened, when there is a page for it. */
function routeFor(entry: AuditLogEntry): string | null {
  switch (entry.aggregateType) {
    case 'Order':
      return `/orders/${entry.aggregateId}`;
    case 'Party':
      // Suppliers live on their own list, but the customer route renders the
      // same page and resolves the party either way.
      return `/customers/${entry.aggregateId}`;
    default:
      return null;
  }
}

function label(map: Record<string, string>, key: string) {
  return map[key] ?? key;
}

/** A date input gives a day; the server wants an instant. */
const startOfDay = (value: string) =>
  value ? new Date(`${value}T00:00:00`).toISOString() : undefined;
const endOfDay = (value: string) =>
  value ? new Date(`${value}T23:59:59.999`).toISOString() : undefined;

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

const QUICK_RANGES: { id: string; label: string; from: () => string }[] = [
  { id: 'today', label: 'Today', from: today },
  { id: '7', label: '7 days', from: () => daysAgo(7) },
  { id: '30', label: '30 days', from: () => daysAgo(30) },
];

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Everything that changed in this organisation, across both services, newest
 * first.
 *
 * <p>
 * The per-record dialogs answer "what happened to this order" and group by save,
 * because that tells one record's story. This answers "what happened at all" —
 * the question you ask when you do not yet know which record to open — so it is
 * a GRID: one row per entry, scannable and comparable like the orders list, with
 * the field-by-field detail a click away rather than making every row a
 * different height.
 * </p>
 *
 * <p>
 * It shows only the record types the reader may already open: the server narrows
 * by the same permission the record's own screen uses, in the query rather than
 * after it, so pages do not arrive half-empty.
 * </p>
 */
export function AuditLogPage() {
  const { user } = useAuth0();
  const navigate = useNavigate();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLTableRowElement | null>(null);

  // Typed into, then applied — refetching both services on every keystroke of a
  // search box would be a request per letter against a growing table.
  const [draft, setDraft] = useState<Filters>(NO_FILTERS);
  const [applied, setApplied] = useState<Filters>(NO_FILTERS);
  const [expanded, setExpanded] = useState<string | null>(null);

  const filters: AuditTrailFilters = useMemo(
    () => ({
      fromUtc: startOfDay(applied.from),
      toUtc: endOfDay(applied.to),
      actor: applied.actor.trim() || undefined,
      action: applied.action || undefined,
      search: applied.search.trim() || undefined,
    }),
    [applied],
  );

  const directory = useDirectoryAuditTrail(orgId, '', null, { allTypes: true, filters });
  const tms = useTmsAuditTrail(orgId, '', null, { allTypes: true, filters });

  const directoryEntries = directory.data ?? [];
  const tmsEntries = tms.data ?? [];

  // Merging two PAGED sources needs a floor, or the middle of the list lies: if
  // one service has been read back to January and the other only to June,
  // everything between shows one service's entries and none of the other's,
  // which reads as a quiet spell rather than as "not asked yet".
  const floors = [
    directory.hasNextPage ? directoryEntries.at(-1)?.occurredUtc : undefined,
    tms.hasNextPage ? tmsEntries.at(-1)?.occurredUtc : undefined,
  ].filter((value): value is string => !!value);
  const floor = floors.length > 0 ? floors.sort().at(-1) : undefined;

  const entries = [...directoryEntries, ...tmsEntries]
    .filter((entry) => !floor || entry.occurredUtc >= floor)
    .sort((a, b) => b.occurredUtc.localeCompare(a.occurredUtc));

  const isFiltered = JSON.stringify(applied) !== JSON.stringify(NO_FILTERS);
  const hasMore = directory.hasNextPage || tms.hasNextPage;
  const isLoading = directory.isPending || tms.isPending;
  const isLoadingMore = directory.isFetchingNextPage || tms.isFetchingNextPage;
  // A re-fetch under new filters is NOT a first load: blanking a list the
  // reader is looking at, to put a spinner where it was, loses their place for
  // a request that usually takes a moment. The rows stay and the bar says it is
  // working.
  const isRefreshing = !isLoading && !isLoadingMore && (directory.isFetching || tms.isFetching);

  const loadMore = () => {
    // Both sides, so the floor moves down together — advancing one would leave
    // the other capping the list and nothing new would appear.
    if (directory.hasNextPage) void directory.fetchNextPage();
    if (tms.hasNextPage) void tms.fetchNextPage();
  };

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || isLoadingMore) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (observed) => {
        if (observed.some((row) => row.isIntersecting)) loadMore();
      },
      { root: scrollRef.current, rootMargin: '200px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isLoadingMore, entries.length]);

  const apply = (next: Filters) => {
    setDraft(next);
    setApplied(next);
  };
  const clear = () => apply(NO_FILTERS);

  const activeRange = QUICK_RANGES.find(
    (range) => applied.from === range.from() && applied.to === '',
  );

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader
        title="Audit Log"
        description="Every change made in this organisation — who, what and when."
      />

      {!orgId && (
        <ErrorBanner
          title="No organisation linked to this account"
          message="Your access token does not include a TMS organisation claim. Contact an administrator."
        />
      )}

      <div className="mb-2 space-y-2 rounded-md border border-slate-200 bg-white p-2">
        {/* Quick ranges rather than a date default. Opening pre-filtered to
            today would greet most visits with an empty list — people come here
            because something changed at some point, rarely today — and a filter
            that looks like "nothing happened" is the worst first impression a
            log can make. Paging already bounds the cost of opening it wide. */}
        <div className="flex flex-wrap items-center gap-1.5">
          {QUICK_RANGES.map((range) => {
            const active = activeRange?.id === range.id;
            return (
              <button
                key={range.id}
                type="button"
                onClick={() => apply({ ...NO_FILTERS, from: range.from() })}
                className={cn(
                  'rounded-full border px-2.5 py-0.5 text-[11px] font-medium',
                  active
                    ? 'border-sky-300 bg-sky-50 text-sky-800'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50',
                )}
              >
                {range.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={clear}
            className={cn(
              'rounded-full border px-2.5 py-0.5 text-[11px] font-medium',
              !isFiltered
                ? 'border-sky-300 bg-sky-50 text-sky-800'
                : 'border-slate-200 text-slate-600 hover:bg-slate-50',
            )}
          >
            All
          </button>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div className="w-36">
            <TextInput
              label="From"
              name="from"
              type="date"
              value={draft.from}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
            />
          </div>
          <div className="w-36">
            <TextInput
              label="To"
              name="to"
              type="date"
              value={draft.to}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
            />
          </div>
          <div className="w-44">
            <TextInput
              label="Who"
              name="actor"
              placeholder="name or email"
              value={draft.actor}
              onChange={(e) => setDraft({ ...draft, actor: e.target.value })}
            />
          </div>
          <div className="w-40">
            <SelectInput
              label="Action"
              name="action"
              value={draft.action}
              options={ACTION_OPTIONS}
              onChange={(e) => setDraft({ ...draft, action: e.target.value as Filters['action'] })}
            />
          </div>
          <div className="min-w-[200px] flex-1">
            <TextInput
              label="Contains"
              name="search"
              placeholder="a reference, a BIC code, a field name…"
              value={draft.search}
              onChange={(e) => setDraft({ ...draft, search: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') apply(draft);
              }}
            />
          </div>

          <button
            type="button"
            onClick={() => apply(draft)}
            disabled={isRefreshing}
            className="flex h-9 items-center gap-1.5 rounded-sm bg-sky-600 px-3 text-xs font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isRefreshing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Search className="h-3.5 w-3.5" />
            )}
            Search
          </button>
          {isFiltered && (
            <button
              type="button"
              onClick={clear}
              className="flex h-9 items-center gap-1 rounded-sm px-2 text-xs font-medium text-slate-500 hover:bg-slate-100"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
          )}
        </div>
      </div>

      {(directory.isError || tms.isError) && (
        <ErrorBanner message="Part of the history could not be loaded, so this list may be incomplete." />
      )}

      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-auto rounded-md border border-slate-200 bg-white"
      >
        <table
          className={cn(
            'w-full table-auto text-xs transition-opacity',
            isRefreshing && 'opacity-60',
          )}
        >
          <thead className="sticky top-0 z-10 bg-slate-700 text-[11px] uppercase tracking-wide text-white">
            <tr>
              <th className="w-8 px-2 py-2" />
              <th className="whitespace-nowrap px-3 py-2 text-left font-semibold">When</th>
              <th className="whitespace-nowrap px-3 py-2 text-left font-semibold">Who</th>
              <th className="whitespace-nowrap px-3 py-2 text-left font-semibold">Record</th>
              <th className="px-3 py-2 text-left font-semibold">Change</th>
              <th className="whitespace-nowrap px-3 py-2 text-left font-semibold">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-3 py-10">
                  <Loading label="Loading the audit log…" size="md" className="justify-center" />
                </td>
              </tr>
            )}

            {!isLoading && entries.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center italic text-slate-400">
                  {isFiltered
                    ? 'Nothing matches those filters.'
                    : 'Nothing recorded yet — the log starts from when auditing was switched on.'}
                </td>
              </tr>
            )}

            {entries.map((entry) => {
              const changes = parseAuditChanges(entry.changesJson);
              const isOpen = expanded === entry.id;
              const route = routeFor(entry);
              const first = changes[0];

              return (
                <Fragment key={entry.id}>
                  <tr
                    onClick={() => setExpanded(isOpen ? null : entry.id)}
                    className="cursor-pointer align-top hover:bg-slate-50"
                  >
                    <td className="px-2 py-2 text-slate-400">
                      {changes.length > 0 &&
                        (isOpen ? (
                          <ChevronDown className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5" />
                        ))}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                      {formatWhen(entry.occurredUtc)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-800">
                      {formatAuditActor(entry.actorKey)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <span className="text-slate-800">
                        {label(AGGREGATE_LABELS, entry.aggregateType)}
                      </span>
                      {entry.entityType !== entry.aggregateType && (
                        <span className="ml-1.5 text-slate-500">
                          · {label(ENTITY_LABELS, entry.entityType)}
                        </span>
                      )}
                      {route && (
                        <button
                          type="button"
                          aria-label="Open the record"
                          title="Open the record"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(route);
                          }}
                          className="ml-1.5 inline-flex text-slate-400 hover:text-sky-700"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {/* One line for one change, a count for many — every row
                          the same height, which is what makes a log scannable.
                          The detail is one click away. */}
                      {changes.length === 0 ? (
                        <span className="italic text-slate-400">—</span>
                      ) : changes.length === 1 && first ? (
                        <span className="whitespace-nowrap">
                          <span className="text-slate-500">{first.field}</span>{' '}
                          {first.snapshot ? (
                            <span className="font-medium text-slate-700">
                              {formatAuditValue(first.to ?? first.from)}
                            </span>
                          ) : (
                            <>
                              <span className="text-slate-400 line-through">
                                {formatAuditValue(first.from)}
                              </span>{' '}
                              <span className="text-slate-400">→</span>{' '}
                              <span className="font-medium text-slate-800">
                                {formatAuditValue(first.to)}
                              </span>
                            </>
                          )}
                        </span>
                      ) : (
                        <span>{changes.length} fields</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <span
                        className={cn(
                          'rounded px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset',
                          ACTION_TONE[entry.action],
                        )}
                      >
                        {entry.action}
                      </span>
                    </td>
                  </tr>

                  {isOpen && changes.length > 0 && (
                    <tr className="bg-slate-50">
                      <td />
                      <td colSpan={5} className="px-3 py-2">
                        <ul className="space-y-0.5">
                          {changes.map((change) => (
                            <li
                              key={change.field}
                              className="flex flex-wrap items-baseline gap-1 text-[11px]"
                            >
                              <span className="text-slate-500">{change.field}</span>
                              {change.snapshot ? (
                                <span className="font-medium text-slate-700">
                                  {formatAuditValue(change.to ?? change.from)}
                                </span>
                              ) : (
                                <>
                                  <span className="text-slate-400 line-through">
                                    {formatAuditValue(change.from)}
                                  </span>
                                  <span className="text-slate-400">→</span>
                                  <span className="font-medium text-slate-800">
                                    {formatAuditValue(change.to)}
                                  </span>
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}

            {/* Loads the next page on approach; the button is not a fallback
                nobody sees — an observer needs layout, and a keyboard reader
                tabbing through never scrolls the container at all. */}
            {hasMore && (
              <tr ref={sentinelRef}>
                <td colSpan={6} className="px-3 py-3 text-center">
                  {isLoadingMore ? (
                    <Loading label="Loading more…" className="justify-center text-[11px]" />
                  ) : (
                    <button
                      type="button"
                      onClick={loadMore}
                      className="text-[11px] font-medium text-purple-600 hover:underline"
                    >
                      Load older entries
                    </button>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
